import React, { useState, useEffect } from 'react';
import { hostingService, HostingPackage } from '../../services/hostingService';
import { getApiUrl } from '../../config/environment';

interface CreatePackageFormData {
  name: string;
  description: string;
  type: 'shared' | 'dedicated' | 'cloud';
  price_monthly: number;
  price_yearly: number;
  disk_space_gb: number;
  bandwidth_gb: number;
  max_domains: number;
  max_email_accounts: number;
  support_level: string;
  features: string[];
}

const HostingManagement: React.FC = () => {
  const [packages, setPackages] = useState<HostingPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState<HostingPackage | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [formData, setFormData] = useState<CreatePackageFormData>({
    name: '',
    description: '',
    type: 'shared',
    price_monthly: 0,
    price_yearly: 0,
    disk_space_gb: 0,
    bandwidth_gb: 0,
    max_domains: 1,
    max_email_accounts: 1,
    support_level: '24/7',
    features: []
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchPackages();
  }, [selectedType]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      let result;
      if (selectedType) {
        result = await fetch(getApiUrl(`/hosting/packages/type/${selectedType}`));
      } else {
        result = await fetch(getApiUrl('/hosting/packages'));
      }
      
      const data = await result.json();
      if (data.success) {
        // Filter out VPS and Email packages - only show hosting-related packages
        const hostingPackages = (data.data || []).filter((pkg: any) => 
          pkg.type === 'shared' || pkg.type === 'dedicated' || pkg.type === 'cloud'
        );
        setPackages(hostingPackages);
      } else {
        setError(data.error || 'Failed to fetch packages');
      }
    } catch (error) {
      setError('Failed to fetch packages');
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/hosting/packages'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          type: formData.type,
          specifications: {
            storage: `${formData.disk_space_gb}GB`,
            bandwidth: formData.bandwidth_gb === -1 ? 'Unlimited' : `${formData.bandwidth_gb}GB`,
            description: `${formData.disk_space_gb}GB Storage, ${formData.bandwidth_gb === -1 ? 'Unlimited' : formData.bandwidth_gb + 'GB'} Bandwidth`
          },
          features: Array.isArray(formData.features) ? formData.features : [],
          priceMonthly: formData.price_monthly,
          priceYearly: formData.price_yearly,
          maxDomains: formData.max_domains,
          maxEmailAccounts: formData.max_email_accounts,
          storageGb: formData.disk_space_gb,
          bandwidthGb: formData.bandwidth_gb,
          supportLevel: formData.support_level
        })
      });

      const result = await response.json();
      if (result.success) {
        setShowCreateForm(false);
        resetForm();
        fetchPackages();
      } else {
        setError(result.error || 'Failed to create package');
      }
    } catch (error) {
      setError('Failed to create package');
      console.error('Error creating package:', error);
    }
  };

  const handleUpdatePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPackage) return;

    try {
      const token = localStorage.getItem('token');
      const updatePayload = {
        name: formData.name,
        description: formData.description,
        type: formData.type,
        specifications: {
          storage: `${formData.disk_space_gb}GB`,
          bandwidth: formData.bandwidth_gb === -1 ? 'Unlimited' : `${formData.bandwidth_gb}GB`,
          description: `${formData.disk_space_gb}GB Storage, ${formData.bandwidth_gb === -1 ? 'Unlimited' : formData.bandwidth_gb + 'GB'} Bandwidth`
        },
        features: Array.isArray(formData.features) ? formData.features : [],
        price_monthly: formData.price_monthly,
        price_yearly: formData.price_yearly,
        max_domains: formData.max_domains,
        max_email_accounts: formData.max_email_accounts,
        storage_gb: formData.disk_space_gb,
        bandwidth_gb: formData.bandwidth_gb,
        support_level: formData.support_level
      };
      
      const response = await fetch(getApiUrl(`/hosting/packages/${editingPackage.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload)
      });

      const result = await response.json();
      if (result.success) {
        setEditingPackage(null);
        resetForm();
        fetchPackages();
      } else {
        setError(result.error || 'Failed to update package');
      }
    } catch (error) {
      setError('Failed to update package');
      console.error('Error updating package:', error);
    }
  };

  const handleDeletePackage = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this package? This action cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      console.log('Delete attempt - Token exists:', !!token);
      console.log('Delete attempt - Package ID:', id);
      
      if (!token) {
        setError('No authentication token found. Please log out and log back in.');
        return;
      }

      const response = await fetch(getApiUrl(`/hosting/packages/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Delete response status:', response.status);
      const result = await response.json();
      console.log('Delete response:', result);
      
      if (result.success) {
        console.log('Delete successful, refreshing packages...');
        fetchPackages();
        setError(''); // Clear any previous errors
      } else {
        const errorMsg = result.error || `Failed to delete package. Status: ${response.status}`;
        setError(errorMsg);
        console.error('Delete failed:', errorMsg);
      }
    } catch (error) {
      const errorMsg = `Failed to delete package: ${error}`;
      setError(errorMsg);
      console.error('Delete error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'shared',
      price_monthly: 0,
      price_yearly: 0,
      disk_space_gb: 0,
      bandwidth_gb: 0,
      max_domains: 1,
      max_email_accounts: 1,
      support_level: '24/7',
      features: []
    });
    setNewFeature('');
  };

  const startEdit = (pkg: HostingPackage) => {
    const parsedFeatures = (() => {
      if (Array.isArray(pkg.features)) {
        return pkg.features;
      } else if (typeof pkg.features === 'string') {
        try {
          const parsed = JSON.parse(pkg.features);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      } else if (pkg.features && typeof pkg.features === 'object') {
        // Handle case where features is an object like {}
        return Object.keys(pkg.features).length === 0 ? [] : [];
      } else {
        return [];
      }
    })();
    
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      type: pkg.type as any,
      price_monthly: pkg.price_monthly || pkg.price,
      price_yearly: pkg.price_yearly || (pkg.price * 10),
      disk_space_gb: pkg.storage_gb || pkg.disk_space_gb || 0,
      bandwidth_gb: pkg.bandwidth_gb || 0,
      max_domains: pkg.max_domains || 0,
      max_email_accounts: pkg.max_email_accounts || 0,
      support_level: pkg.support_level || '',
      features: parsedFeatures
    });
    setShowCreateForm(true);
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
      
      setFormData({
        ...formData,
        features: [...currentFeatures, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const packageTypes = [
    { value: '', label: 'All Hosting Types' },
    { value: 'shared', label: 'Shared Hosting' },
    { value: 'dedicated', label: 'Dedicated Servers' },
    { value: 'cloud', label: 'Cloud Hosting' }
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
        {/* Glossy overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
        <div className="relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
              <span className="text-sm font-semibold text-gray-700">Package Management</span>
            </div>
            <h2 className="text-4xl font-black text-gray-900 mb-2">Hosting Packages</h2>
            <p className="text-xl text-gray-600">Manage web hosting packages and services with advanced controls</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-6 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent text-gray-900 font-medium shadow-sm"
            >
              {packageTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-xl font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
              <span className="relative z-10">Create Package</span>
            </button>
          </div>
        </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError('')} className="text-red-600 text-sm underline mt-2">
            Dismiss
          </button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
          {/* Glossy overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
          <div className="relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-4 shadow-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
              <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {editingPackage ? 'Edit Package' : 'Create New Package'}
            </h3>
            <p className="text-gray-600">Configure your hosting package settings</p>
          </div>
          <form onSubmit={editingPackage ? handleUpdatePackage : handleCreatePackage} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  required
                >
                  <option value="shared">Shared Hosting</option>
                  <option value="dedicated">Dedicated Servers</option>
                  <option value="cloud">Cloud Hosting</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({...formData, price_monthly: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yearly Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_yearly}
                  onChange={(e) => setFormData({...formData, price_yearly: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Storage (GB)</label>
                <input
                  type="number"
                  value={formData.disk_space_gb}
                  onChange={(e) => setFormData({...formData, disk_space_gb: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bandwidth (GB, -1 = Unlimited)</label>
                <input
                  type="number"
                  value={formData.bandwidth_gb}
                  onChange={(e) => setFormData({...formData, bandwidth_gb: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Domains (-1 = Unlimited)</label>
                <input
                  type="number"
                  value={formData.max_domains}
                  onChange={(e) => setFormData({...formData, max_domains: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Email Accounts (-1 = Unlimited)</label>
                <input
                  type="number"
                  value={formData.max_email_accounts}
                  onChange={(e) => setFormData({...formData, max_email_accounts: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Support Level</label>
              <input
                type="text"
                value={formData.support_level}
                onChange={(e) => setFormData({...formData, support_level: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                placeholder="e.g., 24/7, Business Hours, Premium"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  placeholder="Add a feature"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                    <span>{feature}</span>
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
                <span className="relative z-10">{editingPackage ? 'Update Package' : 'Create Package'}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingPackage(null);
                  resetForm();
                }}
                className="border-2 border-orange-400 text-orange-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gradient-to-br hover:from-orange-400 hover:via-yellow-500 hover:to-orange-600 hover:text-white hover:border-transparent transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-white/20 transition-all duration-200 rounded-xl"></div>
                <span className="relative z-10">Cancel</span>
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {packages.map((pkg) => (
          <div key={pkg.id} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 relative overflow-hidden group">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
              <span className={`px-3 py-2 text-sm font-semibold rounded-full shadow-sm ${
                pkg.type === 'shared' ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' :
                pkg.type === 'dedicated' ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white' :
                pkg.type === 'cloud' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' :
                'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
              }`}>
                {pkg.type === 'shared' ? 'Shared' : 
                 pkg.type === 'dedicated' ? 'Dedicated' :
                 pkg.type === 'cloud' ? 'Cloud' : pkg.type}
              </span>
            </div>
            
            <p className="text-gray-700 text-base mb-6 leading-relaxed">{pkg.description}</p>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-base font-medium text-gray-700">Monthly Price:</span>
                <span className="text-2xl font-bold text-primary-navy">${pkg.price_monthly || pkg.price || 0}</span>
              </div>
              {pkg.price_yearly && (
                <div className="flex justify-between items-center mb-4">
                  <span className="text-base font-medium text-gray-700">Yearly Price:</span>
                  <span className="text-lg font-bold text-green-600">${pkg.price_yearly}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Storage</div>
                  <div className="text-lg font-bold text-gray-900">{pkg.storage_gb || pkg.disk_space_gb || 0}GB</div>
                </div>
                <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Bandwidth</div>
                  <div className="text-lg font-bold text-gray-900">
                    {pkg.bandwidth_gb === -1 ? 'Unlimited' : `${pkg.bandwidth_gb || 0}GB`}
                  </div>
                </div>
                <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Domains</div>
                  <div className="text-lg font-bold text-gray-900">
                    {pkg.max_domains === -1 ? 'Unlimited' : (pkg.max_domains || 0)}
                  </div>
                </div>
                <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Emails</div>
                  <div className="text-lg font-bold text-gray-900">
                    {pkg.max_email_accounts === -1 ? 'Unlimited' : (pkg.max_email_accounts || 0)}
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-3 border border-orange-200">
                <span className="text-sm font-semibold text-orange-700">Support Level:</span>
                <span className="text-sm font-bold text-orange-900">{pkg.support_level || 'Not specified'}</span>
              </div>
            </div>

            {(() => {
              // Parse features properly - handle both arrays and JSON strings
              let featuresArray = [];
              if (Array.isArray(pkg.features)) {
                featuresArray = pkg.features;
              } else if (typeof pkg.features === 'string' && pkg.features.trim()) {
                try {
                  const parsed = JSON.parse(pkg.features);
                  featuresArray = Array.isArray(parsed) ? parsed : [];
                } catch {
                  featuresArray = [pkg.features]; // If parsing fails, treat as single feature
                }
              }
              
              return featuresArray.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Features:</h4>
                  <div className="space-y-2">
                    {featuresArray.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full"></div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                    {featuresArray.length > 3 && (
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full"></div>
                        <span className="text-sm text-gray-600 font-medium">+{featuresArray.length - 3} more features...</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="flex space-x-3">
              <button
                onClick={() => startEdit(pkg)}
                className="flex-1 bg-gradient-to-r from-primary-navy to-primary-800 text-white py-3 px-4 rounded-xl font-semibold hover:from-primary-800 hover:to-primary-900 transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"></div>
                <span className="relative z-10">Edit</span>
              </button>
              <button
                onClick={() => handleDeletePackage(pkg.id)}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"></div>
                <span className="relative z-10">Delete</span>
              </button>
            </div>
            </div>
          </div>
        ))}
      </div>

      {packages.length === 0 && !loading && (
        <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-16 shadow-xl text-center relative overflow-hidden">
          {/* Glossy overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-2xl"></div>
          <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
              <svg className="w-12 h-12 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V9M16 13v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2m8 0V9a2 2 0 00-2-2H8a2 2 0 00-2 2v4.01" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">No packages found</h3>
            <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">Create your first hosting package to get started and offer services to your customers.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
              <span className="relative z-10">Create Your First Package</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostingManagement;